/**
 * dateUtils.ts
 *
 * Funciones centralizadas para formateo de fechas con soporte de timezone.
 *
 * DISEÑO:
 * - El backend devuelve strings ISO UTC con sufijo Z (ej: "2026-02-21T01:31:32.580Z")
 *   gracias a que los campos son ahora TIMESTAMPTZ en PostgreSQL.
 * - Las funciones de este módulo reciben ese string + el timezone del negocio
 *   y producen una fecha local correctamente convertida.
 *
 * USO:
 *   import { formatDate, formatDateTime } from '@/utils/dateUtils';
 *   formatDate(audit.start_date, audit.business_timezone)  // "20/02/2026"
 *   formatDateTime(audit.start_date, audit.business_timezone)  // "20/02/2026 22:31"
 */

/** Timezone por defecto cuando el negocio no tiene uno configurado */
export const DEFAULT_TIMEZONE = 'UTC';

/**
 * Parsea cualquier string de fecha proveniente del backend.
 * Soporta tanto ISO con Z ("2026-02-21T01:31:32.580Z") como
 * el formato antiguo sin Z ("2026-02-21 01:31:32.580362").
 *
 * En el segundo caso, lo trata como valor UTC.
 */
function parseDateSafe(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  let str = value.trim();

  // Si no tiene información de zona horaria, asumimos que es UTC
  // (formato antiguo pre-migración o strings sin Z)
  if (!str.endsWith('Z') && !str.includes('+') && !/[+-]\d{2}:\d{2}$/.test(str)) {
    // Reemplazar el espacio por T si es el formato "YYYY-MM-DD HH:MM:SS"
    str = str.replace(' ', 'T') + 'Z';
  }

  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Formatea una fecha en formato dd/MM/yyyy usando el timezone del negocio.
 * Ejemplo: formatDate("2026-02-21T01:31:32Z", "America/Argentina/Buenos_Aires")
 *          → "20/02/2026"
 */
export function formatDate(
  value: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): string {
  const d = parseDateSafe(value);
  if (!d) return '—';

  return new Intl.DateTimeFormat('es-419', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: timezone,
  }).format(d);
}

/**
 * Formatea fecha + hora en formato dd/MM/yyyy HH:mm usando el timezone del negocio.
 * Ejemplo: formatDateTime("2026-02-21T01:31:32Z", "America/Argentina/Buenos_Aires")
 *          → "20/02/2026 22:31"
 */
export function formatDateTime(
  value: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): string {
  const d = parseDateSafe(value);
  if (!d) return '—';

  return new Intl.DateTimeFormat('es-419', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  }).format(d);
}

/**
 * Formatea solo la hora en formato HH:mm usando el timezone del negocio.
 */
export function formatTime(
  value: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): string {
  const d = parseDateSafe(value);
  if (!d) return '—';

  return new Intl.DateTimeFormat('es-419', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  }).format(d);
}

/**
 * Formatea fecha corta: dd/MM HH:mm (para tarjetas y listas compactas)
 */
export function formatDateShort(
  value: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): string {
  const d = parseDateSafe(value);
  if (!d) return '—';

  return new Intl.DateTimeFormat('es-419', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  }).format(d);
}

/**
 * Devuelve cuánto tiempo hace (ej: "hace 2 horas") usando el timezone del negocio.
 */
export function formatRelative(
  value: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): string {
  const d = parseDateSafe(value);
  if (!d) return '—';

  const diff = Date.now() - d.getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours   = Math.floor(diff / 3_600_000);
  const days    = Math.floor(diff / 86_400_000);

  if (minutes < 1)  return 'ahora mismo';
  if (minutes < 60) return `hace ${minutes} min`;
  if (hours < 24)   return `hace ${hours} h`;
  if (days < 7)     return `hace ${days} día${days !== 1 ? 's' : ''}`;

  // Para fechas más antiguas, mostrar la fecha completa
  return formatDate(d, timezone);
}
